/**
 * 全站衰变/合成常量（唯一前端出口）。
 *
 * 权威源在后端（pms-go-api tangentindex 的 ppiCeiling/decayFloor/maxDecayAge/interventionPPI
 * 与 PPI 合成权重），经 GET /tangent-index/decay-config 下发。前端不再各处硬编码，
 * 一律从这里读，避免前后端漂移（历史上 RQI/PPI 分级阈值就因两处各写一份而漂过）。
 *
 * PPI 权重存于 pms_model_config(scene=ppi0)，按道路等级分档（1=主干路、2=次干路、3=高速），
 * 一般设置为 0.70:0.30 或者 0.60:0.40；保存后无需重启即生效。本地默认值与后端
 * internal/ppiweight 的兜底常量一致，仅在接口未就绪/失败时使用。
 *
 * 消费方（地图着色 utils/map/tangentIndex.ts、周度卡片 TangentIndexPointCard.tsx）在“调用时”读取
 * decayConfig.* 字段，因此后端值加载完成后即自动生效，无需重新挂载。
 */
import { tangentIndexApi } from '@/api/tangentIndex'

/** 主干路：权重缺档时的回退目标，与后端 ppiweight.FallbackRoadClassification 一致。 */
export const FALLBACK_ROAD_LEVEL = 1

/** 与后端 ppiweight.DefaultPCIWeight/DefaultRQIWeight 一致的兜底档。 */
const DEFAULT_WEIGHT_PCI = 0.7
const DEFAULT_WEIGHT_RQI = 0.3

export interface DecayWeightRow {
  road_classification: number // 道路功能等级：1=主干路、2=次干路、3=高速
  pci: number                 // 该等级的 PCI 权重
  rqi: number                 // 该等级的 RQI 权重
}

export interface DecayConfig {
  ceiling: number          // 新路上限 95
  /**
   * floor / maxAge 是【展示口径专用】的收口参数：maxAge 是回放着色与设计寿命曲线的
   * 龄期停止线（回放到 15 年为止，超龄老路保持实际年龄、不回压）；floor 只剩设计寿命
   * 曲线类展示在用（回放着色已不设地板，否则会误抬实测低分的老路，首页与看板对不上）。
   * 决策与规划口径两者都不用 —— 那边沿连续曲线一路衰下去，绝对路龄不封顶
   * （后端 tangentindex.ProjectPCI）。别用来算"N 年后 PPI"。
   */
  floor: number            // 展示口径分数下限 40（仅设计寿命曲线）
  maxAge: number           // 展示口径龄期停止线 15 年
  interventionPpi: number  // 干预阈值 60（PPI 衰到此即寿命终点）
  /** 按道路等级分档的 PPI 合成权重，来源 pms_model_config(scene=ppi0)。 */
  weights: DecayWeightRow[]
}

// 可变单例：消费方按引用读取字段，loadDecayConfig 就地覆盖即可全站生效。
export const decayConfig: DecayConfig = {
  ceiling: 95,
  floor: 40,
  maxAge: 15,
  interventionPpi: 60,
  weights: [{ road_classification: FALLBACK_ROAD_LEVEL, pci: DEFAULT_WEIGHT_PCI, rqi: DEFAULT_WEIGHT_RQI }],
}

/** Shared display/business ceiling for PCI and PPI. Missing-value sentinels stay unchanged. */
export const clampMetricScore = (value: number): number =>
  value > decayConfig.ceiling ? decayConfig.ceiling : value

/**
 * 按道路等级取 PPI 合成权重。回退链与后端 internal/ppiweight.resolve 完全一致：
 * 本等级 → 主干路(1) → 默认 0.70/0.30。tangent_level 缺失/未分类(null/0/未知)按主干路处理。
 */
export const weightsForLevel = (tangentLevel?: number | null): { pci: number; rqi: number } => {
  const level = typeof tangentLevel === 'number' && Number.isFinite(tangentLevel)
    ? tangentLevel
    : FALLBACK_ROAD_LEVEL
  const hit = decayConfig.weights.find((row) => row.road_classification === level)
    ?? decayConfig.weights.find((row) => row.road_classification === FALLBACK_ROAD_LEVEL)
  return hit ?? { pci: DEFAULT_WEIGHT_PCI, rqi: DEFAULT_WEIGHT_RQI }
}

/** PPI = w_pci × PCI + w_rqi × RQI，w 按 tangent_level 查表（与后端 ComposePPI 同口径）。 */
export const composePpiScore = (
  pci: number,
  rqi: number,
  tangentLevel?: number | null,
): number => {
  const normalizedPci = clampMetricScore(pci)
  const { pci: weightPci, rqi: weightRqi } = weightsForLevel(tangentLevel)
  const value = weightPci * normalizedPci + weightRqi * rqi
  return clampMetricScore(Math.round(value * 100) / 100)
}

let loaded = false

// 从后端拉取并覆盖本地默认值；失败时静默沿用默认值（与后端一致），不阻塞渲染。
// 幂等：多次调用只真正请求一次。
export async function loadDecayConfig(): Promise<void> {
  if (loaded) return
  await reloadDecayConfig()
}

// 强制重新拉取，绕过上面的幂等守卫。配置页保存权重后调用 —— 后端 ppiweight 已在保存时
// 立即失效重载（见 modelconfig.invalidateWeights），但本 tab 的 loadDecayConfig 是一次性的，
// 不重拉的话本页与已挂载的地图/看板会一直用旧权重直到整页刷新。
// 失败时静默保留旧值，与 loadDecayConfig 同策略。
export async function reloadDecayConfig(): Promise<void> {
  loaded = false
  try {
    const cfg = await tangentIndexApi.getDecayConfig()
    if (cfg && Number.isFinite(cfg.ceiling)) {
      decayConfig.ceiling = cfg.ceiling
      decayConfig.floor = cfg.floor
      decayConfig.maxAge = cfg.max_age
      decayConfig.interventionPpi = cfg.intervention_ppi
      // weights 为空（旧后端）时退化为单档主干路，行为与切换前的全局权重一致。
      decayConfig.weights = Array.isArray(cfg.weights) && cfg.weights.length > 0
        ? cfg.weights.map((row) => ({
            road_classification: row.road_classification,
            pci: row.pci,
            rqi: row.rqi,
          }))
        : [{ road_classification: FALLBACK_ROAD_LEVEL, pci: cfg.weight_pci, rqi: cfg.weight_rqi }]
      loaded = true
    }
  } catch {
    // 保底：沿用旧值/默认值。下次调用可再试。
  }
}
